// @ts-nocheck

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Helper functions for Klaviyo API
const processFields = (fields) => {
  const emptyFields = fields
    ? Object.keys(fields).filter((key) => !fields[key])
    : [];
  const emptyFieldsString =
    emptyFields.length > 0 ? `Missing fields: ${emptyFields.join(", ")}` : "";
  return { emptyFields, emptyFieldsString };
};

const validateEmail = (email) => emailRegex.test(email);

const processResponse = async (responsePromise) => {
  const res = await responsePromise;

  if (!res.ok) {
    throw new Error(`Server responded with status: ${res.status}`);
  }

  if (res.status === 202) {
    return { message: "Request is being processed.", code: 202 };
  }

  // Check if there is any content to parse
  const contentType = res.headers.get("Content-Type");
  if (contentType && contentType.includes("application/json")) {
    try {
      const responseData = await res.json();
      return responseData;
    } catch (error) {
      console.error("Error parsing JSON:", error);
      throw new Error("Failed to parse response.");
    }
  } else {
    console.log("No JSON content to parse or unexpected content type.");
    return null;
  }
};

const subscribe = async (fields, params = {}) => {
  console.log("subscribing to Klaviyo", fields, params);
  const { emptyFields, emptyFieldsString } = processFields(fields);
  if (emptyFields.length) return { error: emptyFieldsString };

  const { email, listId, companyId } = fields;
  if (!validateEmail(email)) return { error: "Invalid email address" };

  const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`;
  const options = {
    method: "POST",
    headers: {
      accept: "application/json",
      revision: "2024-07-15",
      "content-type": "application/json",
    },
    body: JSON.stringify({
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: { properties: params, email },
            },
          },
        },
        relationships: { list: { data: { type: "list", id: listId } } },
      },
    }),
  };

  try {
    const response = await fetch(url, options);
    return processResponse(response);
  } catch (error) {
    console.error("Network or other error:", error);
    return { error: error.message || "Unknown error occurred" };
  }
};

// Web Component
class KlaviyoNewsletter extends HTMLElement {
  constructor() {
    super();

    // State
    this.state = {
      email: "",
      isFocused: false,
      error: "",
      loading: false,
      success: false,
    };
  }

  connectedCallback() {
    // Configuration from attributes - read them here when element is connected
    this.companyId = this.getAttribute("company-id")?.trim() || "";
    this.listId = this.getAttribute("list-id")?.trim() || "";

    this.cacheElements();
    this.attachEventListeners();
    this.updateUI();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  cacheElements() {
    console.log("running cache elements");
    // Look for elements using data attributes
    this.emailInput = this.querySelector("[data-email-input]");
    this.submitButton = this.querySelector("[data-submit-button]");
    this.errorContainer = this.querySelector("[data-error-message]");
    this.successContainer = this.querySelector("[data-success-message]");
    this.loadingContainer = this.querySelector("[data-loading-message]");

    if (!this.emailInput) {
      console.error(
        "KlaviyoNewsletter: No element with [data-email-input] found"
      );
    }
    if (!this.submitButton) {
      console.error(
        "KlaviyoNewsletter: No element with [data-submit-button] found"
      );
    }
  }

  attachEventListeners() {
    if (!this.emailInput || !this.submitButton) return;

    this.handleInput = (e) => {
      this.state.email = e.target.value;
    };

    this.handleFocus = () => {
      this.state.isFocused = true;
    };

    this.handleBlur = () => {
      this.state.isFocused = false;
    };

    this.handleKeydown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.submit();
      }
    };

    this.handleSubmit = (e) => {
      e.preventDefault();
      this.submit();
    };

    this.emailInput.addEventListener("input", this.handleInput);
    this.emailInput.addEventListener("focus", this.handleFocus);
    this.emailInput.addEventListener("blur", this.handleBlur);
    this.emailInput.addEventListener("keydown", this.handleKeydown);
    this.submitButton.addEventListener("click", this.handleSubmit);
  }

  removeEventListeners() {
    if (this.emailInput && this.submitButton) {
      this.emailInput.removeEventListener("input", this.handleInput);
      this.emailInput.removeEventListener("focus", this.handleFocus);
      this.emailInput.removeEventListener("blur", this.handleBlur);
      this.emailInput.removeEventListener("keydown", this.handleKeydown);
      this.submitButton.removeEventListener("click", this.handleSubmit);
    }
  }

  updateUI() {
    // Update error message
    if (this.errorContainer) {
      this.errorContainer.textContent = this.state.error;
      this.errorContainer.style.display = this.state.error ? "block" : "none";
      if (this.state.error) {
        this.errorContainer.setAttribute("role", "alert");
      }
    }

    // Update success message
    if (this.successContainer) {
      this.successContainer.style.display = this.state.success
        ? "block"
        : "none";
      if (this.state.success) {
        this.successContainer.setAttribute("role", "status");
      }
    }

    // Update loading state
    if (this.loadingContainer) {
      this.loadingContainer.style.display = this.state.loading
        ? "block"
        : "none";
    }

    // Show/hide elements based on success state
    if (this.state.success) {
      // Show elements with data-form-success-show
      document.querySelectorAll("[data-form-success-show]").forEach((el) => {
        el.style.display = "block";
      });

      // Hide elements with data-form-success-hide
      document.querySelectorAll("[data-form-success-hide]").forEach((el) => {
        el.style.display = "none";
      });
    } else {
      // Hide elements with data-form-success-show
      document.querySelectorAll("[data-form-success-show]").forEach((el) => {
        el.style.display = "none";
      });

      // Show elements with data-form-success-hide
      document.querySelectorAll("[data-form-success-hide]").forEach((el) => {
        el.style.display = "block";
      });
    }

    // Update button state
    if (this.submitButton) {
      this.submitButton.disabled = this.state.loading;

      // Toggle loading class for styling
      if (this.state.loading) {
        this.submitButton.classList.add("loading");
      } else {
        this.submitButton.classList.remove("loading");
      }
    }

    // Update email input value
    if (this.emailInput && this.state.success) {
      this.emailInput.value = "";
    }
  }

  checkIsValidEmail() {
    if (!this.emailInput) return false;
    return this.emailInput.checkValidity();
  }

  async submit() {
    this.state.error = "";
    this.state.loading = true;
    this.state.success = false;
    this.updateUI();

    // Check for test flags on submit button
    if (this.submitButton) {
      const testSuccess = this.submitButton.hasAttribute(
        "data-submit-test-success"
      );
      const testFail = this.submitButton.hasAttribute("data-submit-test-error");

      if (testSuccess || testFail) {
        // Simulate loading delay
        await new Promise((resolve) => setTimeout(resolve, 1000));

        this.state.loading = false;

        if (testSuccess) {
          // Simulate success
          this.state.email = "";
          this.state.success = true;
          this.dispatchEvent(
            new CustomEvent("subscribed", {
              detail: { email: this.emailInput?.value || "test@example.com" },
              bubbles: true,
            })
          );
        } else {
          // Simulate failure
          this.state.error = "Test error: Subscription failed";
          this.state.success = false;
        }

        this.updateUI();
        return;
      }
    }

    if (!this.checkIsValidEmail()) {
      this.state.error = "Invalid email address";
      this.state.loading = false;
      this.updateUI();
      return;
    }

    try {
      const response = await subscribe({
        email: this.state.email,
        listId: this.listId,
        companyId: this.companyId,
      });

      this.state.loading = false;

      if (response.error) {
        this.state.error = response.error;
        this.state.success = false;
      } else {
        this.state.email = "";
        this.state.success = true;

        // Dispatch custom event for successful subscription
        this.dispatchEvent(
          new CustomEvent("subscribed", {
            detail: { email: this.emailInput.value },
            bubbles: true,
          })
        );
      }
    } catch (error) {
      this.state.error = error.message;
      this.state.loading = false;
      this.state.success = false;
    }

    this.updateUI();
  }
}

// Register the custom element
if (!customElements.get("klaviyo-newsletter")) {
  customElements.define("klaviyo-newsletter", KlaviyoNewsletter);
}
